import { Injectable } from '@angular/core';
import { Resolve } from '@angular/router';
import { Observable, of } from 'rxjs';
import { UserContextService } from 'src/services/user-context.service';
import { RoutePermission } from 'src/app/core/models/route-permission-key';

/**
 * Network Resolver
 *
 * Pre-loads network-related context to prevent FOUC on network routes
 * (entities, carriers, agencies, hierarchies, etc.)
 */
export interface NetworkData {
    isCarrierAccount: boolean;
    isAgencyAccount: boolean;
    hasCarrierViewPermission: boolean;
    hasAgencyViewPermission: boolean;
    hasHierarchyPermission: boolean;
    hasEntitiesPermission: boolean;
    error?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class NetworkResolverService implements Resolve<NetworkData> {

    constructor(
        private userContextService: UserContextService
    ) {}

    resolve(): Observable<NetworkData> {
        try {
            // All permission checks are synchronous
            const isCarrierAccount = this.userContextService.isCarrierAccount();
            const isAgencyAccount = this.userContextService.isAgencyCapabilitiesAccount();
            const hasCarrierViewPermission = this.userContextService.hasPermissionToVisitRoute(RoutePermission.networkCarrierList);
            const hasAgencyViewPermission = this.userContextService.hasPermissionToVisitRoute(RoutePermission.networkAgencyList);
            const hasHierarchyPermission = this.userContextService.hasPermissionToVisitRoute(RoutePermission.networkHierarchyList);
            const hasEntitiesPermission = this.userContextService.hasPermissionToVisitRoute(RoutePermission.networkEntities);

            return of({
                isCarrierAccount,
                isAgencyAccount,
                hasCarrierViewPermission,
                hasAgencyViewPermission,
                hasHierarchyPermission,
                hasEntitiesPermission,
                error: false
            });
        } catch (error) {
            console.error('[NetworkResolver] Failed to resolve network data:', error);
            return of({
                isCarrierAccount: false,
                isAgencyAccount: false,
                hasCarrierViewPermission: false,
                hasAgencyViewPermission: false,
                hasHierarchyPermission: false,
                hasEntitiesPermission: false,
                error: true
            });
        }
    }
}
